import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithHttpStatus } from '../services/baseQuery';
import ApiMiddleWare from '../api-middleware';
import { logError } from '../../util/telemetry/error/log-error.util';
import { GetKioskFlightsParams, GetKioskFlightsResponse, GetKioskFlightReportParams, GetKioskFlightReportResponse, GetKioskUserProfileResponse, CheckDuplicateRunParams, CheckDuplicateRunResponse, SubmitReputationCheckParams, SubmitReputationCheckResponse, GetRunEligibilityResponse, SubmitKioskChatMessageParams, SubmitKioskChatMessageResponse, SendKioskFlightReportEmailResponse, SendKioskFlightReportEmailParams } from '../../kiosk/types/kiosk.types';

// Define a service using a base URL and expected endpoints
export const kioskStore = createApi({
    reducerPath: 'kioskStore',
    baseQuery: baseQueryWithHttpStatus,
      tagTypes: ['kioskFlights', 'duplicateChecks'],
    endpoints: (builder) => ({
        getCompanyThemeDetails: builder.mutation({
            query: (body) => {
                return {
                    url: "kioskBranding/getCompanyThemeDetails/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        checkUserExists: builder.mutation({
            query: (body) => {
                return {
                    url: "kioskBranding/checkUserExists/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        submitReputationCheck: builder.mutation<SubmitReputationCheckResponse, SubmitReputationCheckParams>({
            query: (body) => {
                return {
                    url: "kioskBranding/submitReputationCheck/",
                    method: "POST",
                    body: { ...body }
                }
            },
            invalidatesTags: ['duplicateChecks'],
        }),
        kioskVerifyOTP: builder.mutation({
            query: (body) => {
                return {
                    url: "kiosk/verifyOTP/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        kioskResendOTP: builder.mutation({
            query: (body) => {
                return {
                    url: "kiosk/resendOTP/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        acceptConsent: builder.mutation({
            query: (body) => {
                return {
                    url: "kiosk/acceptConsent/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        getPhoneVerificationDetails: builder.mutation({
            query: (body) => {
                return {
                    url: "kioskBranding/getPhoneVerificationDetails/",
                    method: "POST",
                    body: { ...body }
                }
            },
        }),
        getKioskFlights: builder.query<GetKioskFlightsResponse, GetKioskFlightsParams>({
            query: (params) => ({
                url: "kioskBranding/getKioskFlights/",
                method: "GET",
                params: params
            }),
            providesTags: ['kioskFlights'],
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'getKioskFlights query failed' }); 
                } 
            }
        }),
        getKioskFlightReport: builder.query<GetKioskFlightReportResponse, GetKioskFlightReportParams>({
            query: (params) => ({
                url: "kioskBranding/getKioskFlightReport/",
                method: "GET",
                params: params
            }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'getKioskFlightReport query failed' }); 
                } 
            }
        }),
        getKioskUserProfile: builder.query<GetKioskUserProfileResponse, void>({
            query: () => ({
                url: "kioskBranding/getKioskUserProfile/",
                method: "GET"
            }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'getKioskUserProfile query failed' }); 
                } 
            }
        }),
        checkDuplicateRun: builder.query<CheckDuplicateRunResponse, CheckDuplicateRunParams>({
            query: (params) => ({
                url: "kioskBranding/checkDuplicateRun/",
                method: "GET",
                params: params
            }),
            providesTags: ['duplicateChecks'],
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'checkDuplicateRun query failed' }); 
                } 
            },
        }),
        getRunEligibility: builder.query<GetRunEligibilityResponse, void>({
            query: () => ({
                url: "kiosk/runEligibility/",
                method: "GET"
            }),
            providesTags: ['kioskFlights'], // refetches when flights are submitted
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'getRunEligibility query failed' }); 
                } 
            },
        }),
        submitKioskChatMessage: builder.mutation<SubmitKioskChatMessageResponse, SubmitKioskChatMessageParams>({
            query: (body) => ({
                url: "kiosk/submitKioskChatMessage/",
                method: "POST",
                body
            }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'submitKioskChatMessage query failed' }); 
                } 
            },
        }),
        sendKioskFlightReportEmail: builder.query<SendKioskFlightReportEmailResponse, SendKioskFlightReportEmailParams>({
            query: (params) => ({
                url: "kioskBranding/sendKioskFlightReportEmail/",
                method: "GET",
                params: params
            }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => { 
                try { 
                    ApiMiddleWare(await queryFulfilled, dispatch); 
                } catch (error: unknown) { 
                    logError(error, { category: 'api_http', context: 'sendKioskFlightReportEmail query failed' }); 
                } 
            },
        }),
    }),
})

export const {
    useGetCompanyThemeDetailsMutation,
    useCheckUserExistsMutation,
    useSubmitReputationCheckMutation,
    useKioskVerifyOTPMutation,
    useKioskResendOTPMutation,
    useAcceptConsentMutation,
    useGetPhoneVerificationDetailsMutation,
    useGetKioskFlightsQuery,
    useGetKioskFlightReportQuery,
    useGetKioskUserProfileQuery,
    useCheckDuplicateRunQuery,
    useGetRunEligibilityQuery,
    useSubmitKioskChatMessageMutation,
    useSendKioskFlightReportEmailQuery,
    useLazySendKioskFlightReportEmailQuery
} = kioskStore;